import {
  ChangeEvent, Dispatch, useRef, useState,
} from 'react';
import {
  Row, Col, TextInput,
  Button,
  Card,
  SummaryList,
} from 'nhsuk-react-components';
import { RuleEditor } from '../../../../../schemas/IterationRule.schema';
import { RuleActions } from '../../../../../reducers/rule/ruleReducer';
import {
  getRuleEditorControl,
  updateRuleComparator,
} from '../../../../../utils/ruleEditorUtils';
import { ErrorsDisplay } from '../ErrorsDisplay';
import ListItem from './ListItem';
import './ListEditor.scss';

export interface ListEditorProps {
  ruleEditor?: RuleEditor,
  dispatch: Dispatch<RuleActions>;
  value: string;
  errors: Record<string, string>;
}

export function ListEditor({
  ruleEditor,
  dispatch,
  value,
  errors,
}: ListEditorProps) {
  const [inputValue, setInputValue] = useState('');
  const [itemToEdit, setItemToEdit] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  let valueArray: string[] = [];

  if (Array.isArray(value)) {
    valueArray = value;
  } else if (typeof value === 'string') {
    const cleanedValue = value.endsWith(',') ? value.slice(0, -1) : value;
    valueArray = cleanedValue.split(',').map((v) => v.trim()).filter(Boolean);
  }

  const {
    label, placeholderText, helperText,
  } = getRuleEditorControl(ruleEditor, 'TEXT');

  const {
    label: listLabel,
  } = getRuleEditorControl(ruleEditor, 'LIST');

  const handleAdd = () => {
    // Split by space instead of newline, and add comma after each item
    const newItems = inputValue
      .split(/[\s,]+/)
      .map((item) => item.trim())
      .filter(Boolean);

    const allValues = Array.from(
      new Set([...valueArray, ...newItems]),
    );

    updateRuleComparator(dispatch, ruleEditor, '', allValues);
    setInputValue('');
    inputRef.current?.focus();
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
  };

  const handleDelete = (item: string): void => {
    const updatedValues = valueArray.filter((v) => v !== item);
    updateRuleComparator(dispatch, ruleEditor, '', updatedValues);
    inputRef.current?.focus();
  };

  const handleEdit = (item: string): void => {
    setItemToEdit(item);
    inputRef.current?.focus();
  };

  const handleSaveEdit = (newValue: string): void => {
    const updatedValues = valueArray.map((v) => (v === itemToEdit ? newValue : v));
    updateRuleComparator(dispatch, ruleEditor, '', updatedValues);
    setItemToEdit(null);
    inputRef.current?.focus();
  };

  const handleCancelEdit = (): void => {
    setItemToEdit(null);
    inputRef.current?.focus();
  };

  return (
    <div className="list-editor-container">
      <Row>
        <Col width="full">
          <TextInput
            id="rule-comparator"
            data-testid="rule-comparator"
            aria-label="Rule Comparator"
            label={label}
            placeholder={placeholderText}
            hint={helperText}
            value={inputValue}
            onChange={handleChange}
            ref={inputRef}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAdd();
              }
            }}
          />
        </Col>
        <Col width="full">
          <Button
            data-testid="add-button"
            className="smaller-button"
            onClick={handleAdd}
            aria-label="Add item"
          >
            Add
          </Button>
        </Col>

      </Row>
      <Row>
        <Col width="full">
          <Card cardType="non-urgent">
            <Card.Heading>
              <p className="nhsuk-u-margin-bottom-0">
                {listLabel}
                {' -  '}
                Number of items selected
                (
                {valueArray.length}
                )
              </p>
            </Card.Heading>
            <div className="nhsuk-u-padding-bottom-1">
              <SummaryList>
                {valueArray.map((item: string, idx: number) => (
                  <ListItem
                    value={item}
                    key={item}
                    isEditing={item === itemToEdit}
                    onEdit={() => handleEdit(item)}
                    onDelete={() => handleDelete(item)}
                    onSave={(newValue) => handleSaveEdit(newValue)}
                    onCancel={() => handleCancelEdit()}
                    isLastItem={idx === valueArray.length - 1}
                  />
                ))}
              </SummaryList>
            </div>
          </Card>
        </Col>
      </Row>
      <ErrorsDisplay errors={errors} errorKeys={['Comparator', 'AttributeName']} />
    </div>
  );
}
